/**
 * Trip Storage Schema Definitions
 * 
 * Defines the versioned schema for trip data persistence
 * with migration functions between versions
 */

import { TripData } from '../pages/AccountPage';
import { TransitRouteData, TransportConnectorData } from '../contexts/TripContext';

// Current schema version
export const CURRENT_TRIP_STORAGE_VERSION = 1;

// Storage key
export const TRIP_STORAGE_KEY = 'trekova:trip:v1';

/**
 * Complete trip storage schema (v1)
 * Includes all data needed to restore TripOverview state
 */
export interface TripStorageSchemaV1 {
  // Core trip data
  tripData: TripData | null;
  
  // Transit routes between cities
  transitRoutes: { [key: string]: TransitRouteData };
  
  // Transport connector data for intercity transport
  transportConnectorData: { [key: number]: TransportConnectorData };
  
  // Accommodation search results cache (optional, for performance)
  accommodationSearchResults?: { [cityName: string]: any[] };
  
  // Loading states (for UI consistency)
  loadingStates: {
    isFlightsLoading: boolean;
    hasInitialFlightSearchCompleted: boolean;
    isAccommodationsLoading: boolean;
    hasInitialAccommodationSearchCompleted: boolean;
  };
  
  // Metadata
  lastSaved: number;
  userAgent?: string;
}

/**
 * Type alias for the current version
 */
export type CurrentTripStorageSchema = TripStorageSchemaV1;

/**
 * Default/empty trip storage state
 */
export const getDefaultTripStorage = (): TripStorageSchemaV1 => ({
  tripData: null,
  transitRoutes: {},
  transportConnectorData: {},
  accommodationSearchResults: {},
  loadingStates: {
    isFlightsLoading: false,
    hasInitialFlightSearchCompleted: false,
    isAccommodationsLoading: false,
    hasInitialAccommodationSearchCompleted: false,
  },
  lastSaved: Date.now(),
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
});

/**
 * Serialize trip data for storage
 * Handles Date objects and other non-JSON types
 */
export function serializeTripData(data: TripStorageSchemaV1): string {
  try {
    // Deep clone to avoid mutating original data
    const cloned = JSON.parse(JSON.stringify(data, (key, value) => {
      // Convert Date objects to ISO strings
      if (value instanceof Date) {
        return { __type: 'Date', value: value.toISOString() };
      }
      return value;
    }));
    
    return JSON.stringify(cloned);
  } catch (error) {
    console.error('[TripStorage] Error serializing trip data:', error);
    throw error;
  }
}

/**
 * Deserialize trip data from storage
 * Restores Date objects and validates structure
 */
export function deserializeTripData(jsonString: string): TripStorageSchemaV1 {
  try {
    const parsed = JSON.parse(jsonString, (key, value) => {
      // Restore Date objects
      if (value && typeof value === 'object' && value.__type === 'Date') {
        return new Date(value.value);
      }
      return value;
    });
    
    // Validate and restore required structure
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid storage structure');
    }
    
    // Ensure dates in tripData are Date objects
    if (parsed.tripData?.tripDetails) {
      const { startDate, endDate } = parsed.tripData.tripDetails;
      
      if (startDate && !(startDate instanceof Date)) {
        parsed.tripData.tripDetails.startDate = new Date(startDate);
      }
      if (endDate && !(endDate instanceof Date)) {
        parsed.tripData.tripDetails.endDate = new Date(endDate);
      }
    }
    
    // Restore dates in cityDayDetails
    if (parsed.tripData?.cityDayDetails) {
      Object.keys(parsed.tripData.cityDayDetails).forEach(cityName => {
        const dayDetails = parsed.tripData.cityDayDetails[cityName];
        if (Array.isArray(dayDetails)) {
          dayDetails.forEach((day: any) => {
            if (day.date && !(day.date instanceof Date)) {
              day.date = new Date(day.date);
            }
          });
        }
      });
    }
    
    return parsed as TripStorageSchemaV1;
    
  } catch (error) {
    console.error('[TripStorage] Error deserializing trip data:', error);
    throw error;
  }
}

/**
 * Migration function for future schema versions
 * 
 * @param oldData - Data from old version
 * @param oldVersion - Old schema version number
 * @param newVersion - Target schema version number
 * @returns Migrated data
 */
export function migrateTripStorage(
  oldData: any,
  oldVersion: number,
  newVersion: number
): CurrentTripStorageSchema {
  console.log(`[TripStorage] Migrating from v${oldVersion} to v${newVersion}`);
  
  let migratedData = { ...oldData };
  
  // Migration path from v0 (legacy unsavedTripData) to v1
  if (oldVersion === 0 && newVersion >= 1) {
    console.log('[TripStorage] Migrating legacy unsavedTripData to v1 schema');
    
    // Legacy data might just be TripData without the full schema structure
    if (oldData && !oldData.tripData && oldData.selectedCities) {
      // This is legacy direct TripData format
      migratedData = {
        tripData: oldData,
        transitRoutes: oldData.transitRoutes || {},
        transportConnectorData: oldData.transportConnectorData || {},
        accommodationSearchResults: {},
        loadingStates: {
          isFlightsLoading: false,
          hasInitialFlightSearchCompleted: !!oldData.outboundFlight || !!oldData.inboundFlight,
          isAccommodationsLoading: false,
          hasInitialAccommodationSearchCompleted: true,
        },
        lastSaved: Date.now(),
      };
    } else if (oldData && oldData.tripData) {
      // Already has some structure, just ensure all fields exist
      migratedData = {
        ...getDefaultTripStorage(),
        ...oldData,
        lastSaved: Date.now(),
      };
    }
  }
  
  // Future migrations can be added here
  // Example:
  // if (oldVersion < 2 && newVersion >= 2) {
  //   migratedData = migrateV1ToV2(migratedData);
  // }
  
  return migratedData as CurrentTripStorageSchema;
}

/**
 * Validate trip storage data structure
 * Returns true if valid, false otherwise
 */
export function validateTripStorage(data: any): data is TripStorageSchemaV1 {
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  // Check required top-level fields
  if (!('tripData' in data) || !('transitRoutes' in data)) {
    console.warn('[TripStorage] Missing required fields in storage data');
    return false;
  }
  
  // Validate tripData structure if it exists
  if (data.tripData !== null) {
    const tripData = data.tripData;
    
    if (!tripData.selectedCities || !Array.isArray(tripData.selectedCities)) {
      console.warn('[TripStorage] Invalid selectedCities in tripData');
      return false;
    }
    
    if (!tripData.tripDetails || typeof tripData.tripDetails !== 'object') {
      console.warn('[TripStorage] Invalid tripDetails in tripData');
      return false;
    }
  }
  
  return true;
}

/**
 * Clean/compress trip storage data before saving
 * Removes unnecessary cached data to save space
 */
export function compressTripStorage(data: TripStorageSchemaV1): TripStorageSchemaV1 {
  const compressed = { ...data };
  
  // Remove accommodation search results cache (can be regenerated)
  // Only keep if explicitly needed for performance
  if (compressed.accommodationSearchResults) {
    const totalCacheSize = Object.keys(compressed.accommodationSearchResults).length;
    if (totalCacheSize > 5) {
      // Keep only the 5 most recent searches
      console.log(`[TripStorage] Compressing accommodation cache from ${totalCacheSize} to 5 entries`);
      compressed.accommodationSearchResults = {};
    }
  }
  
  return compressed;
}

